<template>
  <FormContainer :submit="onSubmit">
    <template v-if="error">
      <hg-alert hg-type="error" :hg-value="true">
        We're sorry, but we couldn't retrieve Appointment slots
        <template v-if="getCompany.name">
          for <em>{{getCompany.name}}</em>
        </template>.
        Please try again later
        <template v-if="getCompany.phone">
          or try contacting directly at
          <a :href="`tel:${getCompany.phone}`">{{getCompany.phone}}</a>
        </template>.
      </hg-alert>
    </template>

    <template v-if="loading">
      <v-layout row wrap>
        <v-flex xs12>
          <ScheduleFormLoader />
        </v-flex>
      </v-layout>
    </template>

    <template v-if="showAppointments">
      <v-layout row wrap>
        <v-flex xs12>
          <InspectorSelector
            v-model="filterInspector"
            :inspectors="inspectors"
            :selectedInspector="selectedInspector"
            @change="selectInspector" />
        </v-flex>

        <v-flex xs12>
          <ScheduleFormCalendar @input="selectDate" />
        </v-flex>
      </v-layout>

      <ScheduleFormTimeSelection
        @dateBack="selectPreviousDate"
        @dateForward="selectNextDate"
        @update="selectTime" />
    </template>

    <StepNavigation
      :continueDisabled="disableSubmitButton"
      :hideBack="true"
      :submitMode="true"
      @submit="onSubmit"
      @save="save" />
  </FormContainer>
</template>

<script>
import { Components, Mixins } from '@spectorasoftware/homegauge-vue';
import { mapActions, mapGetters, mapState } from 'vuex';

import FormContainer from '@/components/FormContainer.vue';
import StepNavigation from '@/components/StepNavigation.vue';
import InspectorSelector from '@/components/InspectorSelector.vue';
import ScheduleFormCalendar from '@/components/ScheduleFormCalendar.vue';
import ScheduleFormLoader from '@/components/ScheduleFormLoader.vue';
import ScheduleFormTimeSelection from '@/components/ScheduleFormTimeSelection.vue';

const { BaseAlert } = Components;
const { DateTime } = Mixins;

const DEFAULT_FORM = {
  chosenDate: DateTime.methods.dashDate(new Date()),
  inspectorId: null,
  time: '',
};

// @TODO Convert filters to be local
export default {
  components: {
    FormContainer,
    InspectorSelector,
    ScheduleFormCalendar,
    ScheduleFormLoader,
    ScheduleFormTimeSelection,
    StepNavigation,
    'hg-alert': BaseAlert,
  },
  props: {
    model: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      form: DEFAULT_FORM,
    };
  },
  mixins: [DateTime],
  async mounted() {
    this.form.chosenDate = this.model.chosenDate;
    this.form.inspectorId = this.model.inspectorId;
    this.form.time = this.model.time;
    await this.retrieveAvailabilities();
    await this.retrieveCustomFields();
  },
  computed: {
    ...mapGetters('Company', [
      'getCompany',
    ]),
    ...mapGetters('Schedule', [
      'filteredDates',
      'filteredTimeslots',
      'nextAvailability',
      'prevAvailability',
      'selectedInspector',
      'selectedAvailabilityExists',
      'selectedTimeslot',
    ]),
    ...mapState('Schedule', {
      error: state => state.error,
      loading: state => state.loading,
      filterDate: state => state.filters.date,
      filterInspector: state => state.filters.inspector,
      filterMonth: state => state.filters.month,
      filterTime: state => state.filters.time,
      inspectors: state => state.inspectors,
    }),
    disableSubmitButton() {
      return !!(this.error || this.loading || !this.hasTimeSlots);
    },
    hasTimeSlots() {
      return !!(this.filteredTimeslots && this.filteredTimeslots.length > 0);
    },
    showAppointments() {
      return !!(!this.error && !this.loading);
    },
  },
  methods: {
    ...mapActions('Schedule', [
      'getAvailabilities',
      'selectFirstAvailability',
      'selectFirstAvailabilityForDate',
      'setDateFilter',
      'setInspectorFilter',
      'setTimeFilter',
    ]),
    ...mapActions('Appointment', [
      'loadCustomFields',
    ]),
    async retrieveAvailabilities() {
      await this.getAvailabilities(this.getCompany.id);
      if (!this.selectedAvailabilityExists) {
        const action = this.filterDate
          ? this.selectFirstAvailabilityForDate
          : this.selectFirstAvailability;
        action();
      }
    },
    async retrieveCustomFields() {
      await this.loadCustomFields(this.getCompany.companyId);
    },
    selectDate(date) {
      if (date === this.filterDate) {
        return;
      }

      this.setDateFilter(date)
        .then(() => this.selectFirstAvailabilityForDate());
    },
    selectInspector(inspectorId) {
      if (inspectorId === this.filterInspector) {
        return;
      }

      this.setInspectorFilter(inspectorId)
        .then(() => {
          if (!this.selectedAvailabilityExists) {
            this.selectFirstAvailabilityForDate();
          }
        });
    },
    selectTime(time) {
      if (time === this.filterTime) {
        return;
      }

      this.setTimeFilter(time);
    },
    selectNextDate() {
      if (this.nextAvailability) {
        this.setDateFilter(this.nextAvailability)
          .then(() => this.selectFirstAvailabilityForDate());
      }
    },
    selectPreviousDate() {
      if (this.prevAvailability) {
        this.setDateFilter(this.prevAvailability)
          .then(() => this.selectFirstAvailabilityForDate());
      }
    },
    onSubmit() {
      this.$emit('submit', this.selectedTimeslot);
    },
    save() {
      this.$emit('save', this.selectedTimeslot);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../styles/vars/theme';
@import '../../styles/vars/typography';

.time-picker {
  align-items: center;
  display: flex;
}

.time-display {
  font-size: 16px;
  font-weight: 600;
}

.optional-label {
  color: $theme-color-secondary;
  font-style: italic;
}

.alternate-date-label {
  display: inline-block;
}

.white {
  color: #2196f3;
}

.select-date {
  background-color: $theme-color-primary;
  padding-top: 10px;
  padding-bottom: 10px;
  font-family: $typography-font-primary;
  text-transform: uppercase;
  color: $theme-color-black;
  font-weight: 600;
  letter-spacing: 1px;
  font-size: 14px;
  border-radius: 4px;
  width: 97%;
}

.short-date-string {
  text-align: center;
  font-size: 2em;
}

.floating-continue {
  bottom: 10px;
  color: $theme-color-black;
  left: 1vw;
  position: fixed;
  width: 98vw;
}
</style>
